import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, FormControlLabel, Switch, Box, Grid, FormHelperText } from '@mui/material';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';
import { useTranslation } from 'services/localization/useTranslation';
import { shopHasPaymentMethod } from 'common/modules/payments/paymentMethods';
import { getPaymentCommissionString } from 'common/modules/payments/utils';
import { api } from 'common/frontend/api';
import {
	getDefaultCommission,
	getShopCustomPaymentCommission,
	planToPaymentPlan,
} from 'common/constants/paymentCommissions';
import { LocaleHelpers } from 'common/locales/helpers';
import { ShopOnlinePaymentMethod } from 'common/modules/payments/types';

export interface PaymentMethodRowInfo {
	name: ShopOnlinePaymentMethod;
	description?: string;
	hidden?: boolean;
	disabled?: boolean;
	helperText?: string;
	showPricing?: boolean;
	pricingDescription?: string;
}

export const PaymentMethodRow = (props: PaymentMethodRowInfo) => {
	const {
		name,
		description,
		hidden,
		disabled,
		helperText,
		showPricing = true,
		pricingDescription,
	} = props;
	const selectedPaymentMethods = useSelector(ShopSelectors.getActivePaymentMethods);
	const dispatch = useDispatch();
	const shop = useSelector(ShopSelectors.activeShopData)!;
	const plan = useSelector(ShopSelectors.shopPlan);
	const shopPaymentCommission = useSelector(ShopSelectors.activeShopPaymentMethodCommission);
	const paymentPlan = planToPaymentPlan(plan);
	const shopCurrency = shop.publicInfo.currency;
	const { t } = useTranslation();
	const commission =
		getShopCustomPaymentCommission(name, shopPaymentCommission) ??
		getDefaultCommission(paymentPlan, shopCurrency.code, name);

	const handlePaymentMethodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const paymentMethod = e.target.value as ShopOnlinePaymentMethod;
		let newPaymentMethods = selectedPaymentMethods.filter((method) => method.id !== paymentMethod);
		if (!shopHasPaymentMethod(paymentMethod, selectedPaymentMethods)) {
			newPaymentMethods = [...newPaymentMethods, { id: paymentMethod }];
		}
		try {
			api().shops.doc(shop.publicInfo.shopId).public.update({
				activePaymentMethods: newPaymentMethods,
			});
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
	};

	return !!hidden ? null : (
		<Box py={1}>
			<Grid container spacing={0} alignItems="center">
				<Grid item xs={12} sm>
					<FormControlLabel
						control={
							<Switch
								color="primary"
								checked={shopHasPaymentMethod(name, selectedPaymentMethods)}
								onChange={handlePaymentMethodChange}
								value={name}
								disabled={!!disabled}
							/>
						}
						label={
							<>
								<Typography>
									{LocaleHelpers.admin(t).paymentViewMethodSelection[name] ?? name}
								</Typography>
								{description && (
									<Typography color="secondary" variant="body2">
										{description}
									</Typography>
								)}
							</>
						}
					/>
					{helperText && <FormHelperText>{helperText}</FormHelperText>}
				</Grid>
				{showPricing && (
					<Grid item xs={12} sm="auto">
						<Typography align="right">{getPaymentCommissionString(commission)}</Typography>
						{pricingDescription && (
							<Typography align="right" color="secondary" variant="caption">
								{pricingDescription}
							</Typography>
						)}
					</Grid>
				)}
			</Grid>
		</Box>
	);
};
